/**
 * **Tasks ▸ preferences** (`docs/13-tasks-section-proposal.md` §2, §5,
 * `specs/M10-tasks.md` §5).
 *
 * Six per-user settings, every one of them a `tasks.*` key in `user_preferences`, written
 * through `tasks.settings.set`. They are **not** in Profile ▸ Notifications: that matrix is
 * monday's 31 kinds verbatim and the three Tasks notifications are deliberately outside it
 * (q558), so their switches live here.
 *
 * The archive delay is the one setting with teeth: there is no sweep and no stored archived
 * state, so changing it moves rows in and out of the Archive tab on the next read (§3.3).
 */
import { SlideOverPane } from '@elizmonday/ui';
import { TaskMappingSection, type TaskMappingSectionProps } from './TaskMappingSection';
import {
  TASK_ARCHIVE_DELAYS,
  TASK_DIGEST_FREQUENCIES,
  type TaskArchiveDelay,
  type TaskDigestFrequency,
  type TaskSettingsView,
} from './types';
import { TASK_ARCHIVE_DELAY_LABELS, TASK_DIGEST_LABELS } from './vocabulary';

export interface TaskSettingsPanelProps {
  settings: TaskSettingsView;
  onChange(patch: Partial<TaskSettingsView>): void;
  onClose(): void;
  /**
   * **P-294** — the per-board mapping editor, below the six per-user settings.
   *
   * Optional, and absent in the fixture harness and in the unit tests that only drive the
   * preferences: the mapping is a *board* setting behind `board.settings.update`, not a user
   * one, and a page rendering the pane with no board list has nothing to show. When it is
   * given, the pane is where a person can finally see what "done" means on their boards — which
   * is the whole reason P-294 was a data incident and not a bug report.
   */
  mapping?: TaskMappingSectionProps;
}

/** **P-321** — where the two e-mail switches are also managed (Profile ▸ Notifications). */
export const TASK_NOTIFICATIONS_HREF = '/me/notifications';

/** `08:00` — the digest hour is stored as a local hour, 0–23 (§5). */
export function formatHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

export function TaskSettingsPanel(props: TaskSettingsPanelProps) {
  const { settings, onChange, onClose } = props;
  return (
    <SlideOverPane open title="Tasks preferences" onClose={onClose} width={420} data-testid="tasks-settings">
      <div className="ez-tasks-settings">
        {/*
          **P-321** · the other half of the link. The digest and the overdue reminder are also in
          Profile ▸ Notifications now (one `tasks.settings` store, both surfaces), and somebody
          who came here looking for "my e-mails" should be told where the rest of them are.
        */}
        <p className="ez-tasks-settings__crosslink">
          {'The digest and the overdue reminder are also in '}
          <a href={TASK_NOTIFICATIONS_HREF} data-testid="tasks-settings-notifications-link">
            Profile ▸ Notifications
          </a>
          .
        </p>

        <label className="ez-tasks-settings__row">
          <span className="ez-tasks-settings__label">Move done tasks to Archive</span>
          <span className="ez-tasks-settings__hint">
            A task leaves My Tasks this long after it was completed. Reopening it brings it back
            straight away.
          </span>
          <select
            className="ez-tasks-settings__control"
            data-testid="tasks-settings-archive-delay"
            value={String(settings.archiveDelayDays)}
            onChange={(event) =>
              onChange({ archiveDelayDays: Number(event.target.value) as TaskArchiveDelay })
            }
          >
            {TASK_ARCHIVE_DELAYS.map((delay) => (
              <option key={delay} value={delay}>
                {TASK_ARCHIVE_DELAY_LABELS[delay]}
              </option>
            ))}
          </select>
        </label>

        <label className="ez-tasks-settings__row">
          <span className="ez-tasks-settings__label">Email digest</span>
          <span className="ez-tasks-settings__hint">“Your tasks today”, sent at your own hour.</span>
          <select
            className="ez-tasks-settings__control"
            data-testid="tasks-settings-digest"
            value={settings.digestFrequency}
            onChange={(event) =>
              onChange({ digestFrequency: event.target.value as TaskDigestFrequency })
            }
          >
            {TASK_DIGEST_FREQUENCIES.map((frequency) => (
              <option key={frequency} value={frequency}>
                {TASK_DIGEST_LABELS[frequency]}
              </option>
            ))}
          </select>
        </label>

        {/* P-269: the hour drives the overdue digest too, so it stays live whenever either is on. */}
        <label
          className="ez-tasks-settings__row"
          data-disabled={
            (settings.digestFrequency === 'off' && !settings.remindOverdue) || undefined
          }
        >
          <span className="ez-tasks-settings__label">Send it at</span>
          <select
            className="ez-tasks-settings__control"
            data-testid="tasks-settings-hour"
            value={String(settings.digestHour)}
            disabled={settings.digestFrequency === 'off' && !settings.remindOverdue}
            onChange={(event) => onChange({ digestHour: Number(event.target.value) })}
          >
            {Array.from({ length: 24 }, (_, hour) => (
              <option key={hour} value={hour}>
                {formatHour(hour)}
              </option>
            ))}
          </select>
        </label>

        <label className="ez-tasks-settings__toggle">
          <input
            type="checkbox"
            data-testid="tasks-settings-remind"
            checked={settings.remindOverdue}
            onChange={(event) => onChange({ remindOverdue: event.target.checked })}
          />
          <span>
            <span className="ez-tasks-settings__label">Remind me about overdue tasks</span>
            <span className="ez-tasks-settings__hint">
              One summary a day at the hour above, never one notification per task.
            </span>
          </span>
        </label>

        <label className="ez-tasks-settings__toggle">
          <input
            type="checkbox"
            data-testid="tasks-settings-notify"
            checked={settings.notifyOnComplete}
            onChange={(event) => onChange({ notifyOnComplete: event.target.checked })}
          />
          <span>
            <span className="ez-tasks-settings__label">Tell me when a task I assigned is completed</span>
            <span className="ez-tasks-settings__hint">
              “<em>Someone</em> completed a task you assigned”, in the bell.
            </span>
          </span>
        </label>

        {props.mapping ? <TaskMappingSection {...props.mapping} /> : null}
      </div>
    </SlideOverPane>
  );
}
